import { Dispatch, SetStateAction } from 'react';
import dayjs from 'dayjs';
import { StrategyPhase, WeeklyStrategy } from '../ProfitScenario/types';
import { regenerateWeeks } from './utils';

export const usePhaseOperations = (
    phases: StrategyPhase[],
    setPhases: Dispatch<SetStateAction<StrategyPhase[]>>
) => {
    /**
     * 更新阶段信息（名称、时间范围、开始日期）
     */
    const updatePhaseInfo = (
        phaseId: string,
        updates: Partial<Pick<StrategyPhase, 'name' | 'startWeek' | 'endWeek' | 'startDate' | 'strategyDescription' | 'flowDiagram'>>
    ) => {
        setPhases(prev => {
            const phaseIndex = prev.findIndex(p => p.id === phaseId);
            if (phaseIndex === -1) return prev;

            const newPhases = [...prev];
            const currentPhase = { ...newPhases[phaseIndex], ...updates };

            // 1. 处理当前阶段的周策略生成
            if (updates.startWeek !== undefined || updates.endWeek !== undefined) {
                const startWeek = updates.startWeek ?? currentPhase.startWeek;
                const endWeek = updates.endWeek ?? currentPhase.endWeek;

                if (startWeek >= 1 && endWeek <= 13 && startWeek <= endWeek) {
                    currentPhase.weeks = regenerateWeeks(startWeek, endWeek, currentPhase.weeks);
                    currentPhase.startWeek = startWeek;
                    currentPhase.endWeek = endWeek;
                }
            }
            newPhases[phaseIndex] = currentPhase;

            // 2. 级联更新后续阶段的 startDate
            // 只有当当前阶段有 startDate，或者当前阶段是第一个阶段且被修改了 startDate 时才触发
            if (currentPhase.startDate || (phaseIndex === 0 && updates.startDate)) {
                // 从当前阶段开始，依次向后更新
                for (let i = phaseIndex; i < newPhases.length - 1; i++) {
                    const curr = newPhases[i];
                    const next = newPhases[i + 1];

                    if (curr.startDate) {
                        const currStart = dayjs(curr.startDate);
                        // 计算当前阶段的总天数
                        let totalDays = 0;
                        for (let w = curr.startWeek; w <= curr.endWeek; w++) {
                            totalDays += w === 13 ? 6 : 7;
                        }

                        // 下一阶段的开始日期 = 当前阶段开始日期 + 当前阶段总天数
                        next.startDate = currStart.add(totalDays, 'day').format('YYYY-MM-DD');
                        newPhases[i + 1] = { ...next };
                    }
                }
            }

            return newPhases;
        });
    };

    /**
     * 更新单周策略
     */
    const updateWeekStrategy = (
        phaseId: string,
        weekIndex: number,
        updates: Partial<WeeklyStrategy>
    ) => {
        setPhases(prev => prev.map(phase => {
            if (phase.id === phaseId) {
                const newWeeks = [...phase.weeks];
                newWeeks[weekIndex] = { ...newWeeks[weekIndex], ...updates };
                return { ...phase, weeks: newWeeks };
            }
            return phase;
        }));
    };

    /**
     * 添加新阶段
     */
    const addPhase = () => {
        const newId = String(phases.length + 1);
        const lastPhase = phases[phases.length - 1];
        const nextStartWeek = lastPhase ? lastPhase.endWeek + 1 : 1;

        if (nextStartWeek > 13) return; // 已达到最大周数

        // 自动计算新阶段的开始日期
        let startDate: string | undefined = undefined;
        if (lastPhase?.startDate) {
            const lastPhaseStart = dayjs(lastPhase.startDate);
            // 第13周只有6天，其他周7天
            let totalDays = 0;
            for (let i = lastPhase.startWeek; i <= lastPhase.endWeek; i++) {
                totalDays += i === 13 ? 6 : 7;
            }
            startDate = lastPhaseStart.add(totalDays, 'day').format('YYYY-MM-DD');
        }

        const newPhase: StrategyPhase = {
            id: newId,
            name: `阶段 ${newId}`,
            startWeek: nextStartWeek,
            endWeek: Math.min(nextStartWeek + 3, 13),
            startDate,
            weeks: []
        };

        newPhase.weeks = Array.from(
            { length: newPhase.endWeek - newPhase.startWeek + 1 },
            (_, i) => ({ ...lastPhase.weeks[0], weekNumber: newPhase.startWeek + i })
        );

        setPhases([...phases, newPhase]);
    };

    /**
     * 删除阶段
     */
    const deletePhase = (phaseId: string) => {
        if (phases.length <= 1) return; // 至少保留一个阶段
        setPhases(prev => prev.filter(p => p.id !== phaseId));
    };

    return {
        updatePhaseInfo,
        updateWeekStrategy,
        addPhase,
        deletePhase
    };
};
